import type { PointerEvent as ReactPointerEvent } from 'react';
import { swallowReleaseClick } from './useDragGesture';

/** How long a press rests before it is a long press, in ms. */
export const LONG_PRESS_MS = 450;
/** How far a press may wander and still be a long press, in CSS pixels. */
const SLOP = 8;

/**
 * Watches a press for a long press: one that rests where it began for LONG_PRESS_MS, with a
 * finger, a pen or the mouse. Then `onLong` runs, and the click its release causes is swallowed
 * (it was not a tap). A press that moves away, ends or is cancelled before then is no long press.
 * Returns a function that stops watching.
 */
export function watchLongPress(
  e: PointerEvent | ReactPointerEvent,
  onLong: () => void,
): () => void {
  const id = e.pointerId;
  const from = { x: e.clientX, y: e.clientY };
  let fired = false;
  const move = (ev: PointerEvent) => {
    if (ev.pointerId !== id || fired) return;
    if (Math.hypot(ev.clientX - from.x, ev.clientY - from.y) > SLOP) stop();
  };
  const up = (ev: PointerEvent) => {
    if (ev.pointerId !== id) return;
    stop();
    if (fired && ev.type === 'pointerup') swallowReleaseClick(ev.clientX, ev.clientY);
  };
  const timer = setTimeout(() => {
    fired = true;
    onLong();
  }, LONG_PRESS_MS);
  function stop() {
    clearTimeout(timer);
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    window.removeEventListener('pointercancel', up);
  }
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
  window.addEventListener('pointercancel', up);
  return stop;
}
